import flow from "lodash/flow";
import { checkIsNotUndefined } from "@modules/utilityTypes/checkIsNotUndefined";

import { ReportType } from "./ReportType";
import { BuildConfig } from "./BuildConfig";
import { pickSections } from "./pickSections";
import { Section } from "../fhirQuestionnaire/Section";
import { transformSections } from "./transformSections";
import { Questionnaire } from "../fhirQuestionnaire/Questionnaire";
import { initialReportBuildConfig } from "./initialReportBuildConfig";
import { followUpReportBuildConfig } from "./followUpReportBuildConfig";
import { initialOrthoExtremitiesBuildConfig } from "./initialOrthoExtremitiesBuildConfig";
import { followUpOrthoExtremitiesBuildConfig } from "./followUpOrthoExtremitiesBuildConfig";
export function buildQuestionnaire(
  reportType: ReportType,
  allSectionsInQuestionnaireShape: Questionnaire,
) {
  const buildConfig = resolveReportBuildConfig(reportType);
  const { sections: questionnaireSections, metadata } =
    partitionIntoSectionsAndMetadata(allSectionsInQuestionnaireShape);

  const pick = (sections: Section[]) => {
    return pickSections(buildConfig.pick.topLevelSections.textIds, sections);
  };

  const transform = (sections: Section[]) => {
    if (!buildConfig.transform) {
      return sections;
    }
    return transformSections(sections, buildConfig.transform.topLevelSections);
  };
  const processedSections = processSections(questionnaireSections, {
    buildConfig,
    pick,
    transform,
  });
  const result: Questionnaire = {
    ...metadata,
    item: processedSections,
  };
  return result;
}

function resolveReportBuildConfig(reportType: ReportType) {
  switch (reportType) {
    case ReportType.INITIAL:
      return initialReportBuildConfig;
    case ReportType.FOLLOW_UP:
      return followUpReportBuildConfig;
    case ReportType.INITIAL_ORTHO_EXTREMITIES:
      return initialOrthoExtremitiesBuildConfig;
    case ReportType.FOLLOW_UP_ORTHO_EXTREMITIES:
      return followUpOrthoExtremitiesBuildConfig;
  }
}

function processSections(
  questionnaireSections: Section[],
  {
    buildConfig,
    pick,
    transform,
  }: {
    buildConfig: BuildConfig;
    pick: (sections: Section[]) => Section[];
    transform: (sections: Section[]) => Section[];
  },
) {
  const orderedActions = buildConfig.actionsOrder
    .map((action) => {
      switch (action) {
        case "pick":
          return pick;
        case "transform":
          return transform;
        default:
          return undefined;
      }
    })
    .filter(checkIsNotUndefined);

  const processedSections = flow(...orderedActions)(questionnaireSections);
  return processedSections;
}

function partitionIntoSectionsAndMetadata(questionnaire: Questionnaire) {
  const { item, ...metadata } = questionnaire;
  return { sections: item, metadata };
}
